import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  apiGetEventByEventId,
  apiNextEvent,
  apiSubmitLabel,
} from '../api';
import type { LabelEvent } from '../types';
import VideoLabeler from '../components/VideoLabeler';
import { fmtTime } from '../lib/format';

export default function Label() {
  // URL 的 :eventId 是來源 event_id (例如 207415926),便於分享。
  // 後端內部仍用 events.id (DB pk) 儲存標註,從事件回應的 event.id 取得。
  // Path is /label  or  /label/:taskId/:eventId — both params travel
  // together so a deep-link URL is always unambiguous.
  const { taskId: urlTaskId, eventId: urlEventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState<LabelEvent | null | undefined>(undefined);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setEvent(undefined);
    try {
      if (urlEventId && urlTaskId) {
        const r = await apiGetEventByEventId(Number(urlEventId), Number(urlTaskId));
        setEvent(r.event);
      } else if (urlTaskId) {
        // 任務內佇列模式:抓該任務的下一個未標註事件並重導到
        // /label/:taskId/:eventId。
        const r = await apiNextEvent({ task_id: Number(urlTaskId) });
        if (!r.event) {
          setDone(true);
          return;
        }
        navigate(
          `/label/${urlTaskId}/${r.event.event_id}`,
          { replace: true },
        );
      } else {
        // /label 由 TaskPicker 處理,不會走到這
        setDone(true);
      }
    } catch (e) {
      setError(String(e));
    }
  }, [urlEventId, urlTaskId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // 標完一筆後共用的「進入下一筆」邏輯 — 限定在同一個任務內。
  async function goToNext(currentPk: number) {
    const taskScope = urlTaskId ? Number(urlTaskId) : undefined;
    const r = await apiNextEvent({ task_id: taskScope, exclude: [currentPk] });
    if (!r.event) {
      setDone(true);
      return;
    }
    navigate(`/label/${r.event.task_id}/${r.event.event_id}`);
  }

  if (done) {
    return (
      <div className="card">
        <h2>全部完成</h2>
        <p>此任務已沒有待標註的事件。</p>
        <p>
          <button type="button" onClick={() => navigate('/label')}>← 回到任務列表</button>
        </p>
      </div>
    );
  }
  if (error) return <div className="card error">{error}</div>;
  if (event === undefined) return <div className="card">載入中…</div>;
  if (event === null) return <div className="card">沒有事件。</div>;

  return (
    <div className="card">
      <div className="event-header">
        <span>事件 ID:<b>{event.event_id}</b></span>
        <span>任務:{event.task_id}</span>
        {event.label && event.label.data.unlabelable && (
          <span className="warn">
            先前已標記為「無法標記」
            {event.label.data.reason ? `:${event.label.data.reason}` : ''}
            ,送出將覆寫先前結果
          </span>
        )}
        {event.label && !event.label.data.unlabelable && typeof event.label.data.time_seconds === 'number' && (
          <span className="warn">
            已於 {event.label.data.time_seconds.toFixed(2)} 秒標註,送出將覆寫先前結果
          </span>
        )}
      </div>
      {event.active_claim && (
        <div className="conflict-banner">
          ⚠ 目前由 <b>{event.active_claim.user_email}</b> 標註中
          (claim 於 {fmtTime(event.active_claim.expires_at)} 過期)。
          可繼續觀看影片,但送出標註將會被拒絕,直到對方完成或 claim 過期。
        </div>
      )}
      <VideoLabeler
        videoUrl={event.video_url}
        initialTime={
          event.label && !event.label.data.unlabelable
            ? event.label.data.time_seconds
            : undefined
        }
        onSubmit={async data => {
          await apiSubmitLabel(event.id, data);
          await goToNext(event.id);
        }}
        onMarkUnlabelable={async data => {
          await apiSubmitLabel(event.id, data);
          await goToNext(event.id);
        }}
      />
    </div>
  );
}
